import { Flex, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useMemo } from "react";

import { EnergyLabel } from "../elements/EnergyLabel";
import PriceDisplay from "../elements/PriceDisplay";
import { InfoPopover } from "./InfoPopover";
import { RadialThermometer } from "./RadialThermometer";
import {
  electricityPriceAtom,
  extraCostsAtom,
  gasPriceAtom,
  joulePriceAtom,
} from "../compositions/modals/SettingsModal";
import {
  ResidenceTypeModel,
  ScenarioModel,
  SituationModel,
} from "../clients/KompasApi";
import { TextPopover } from "./TextPopover";

type Props = {
  situation: SituationModel;
  scenario?: ScenarioModel;
  residenceType: ResidenceTypeModel;
};

export function ResidenceBaseBlock({
  situation,
  scenario,
  residenceType,
}: Props) {
  const [gasPrice] = useAtom(gasPriceAtom);
  const [electricityPrice] = useAtom(electricityPriceAtom);
  const [joulePrice] = useAtom(joulePriceAtom);
  const [extraCosts] = useAtom(extraCostsAtom);

  const calculation = useMemo(
    () => scenario?.calculation ?? situation.calculation,
    [scenario, situation],
  );

  const epvPrice = useMemo(() => {
    const category = scenario
      ? scenario.energyPerformanceFeeCategoryName
      : situation.energyPerformanceFeeCategoryName;

    return category ? (extraCosts[category] ?? 0) : 0;
  }, [scenario, situation, extraCosts]);

  return (
    <Flex alignItems="center" flexDir="column" gap={1} py={4}>
      <Text fontWeight={500} h={16} textAlign="center">
        {scenario?.name ?? "Huidige situatie"}
      </Text>

      <RadialThermometer
        currentLabel={situation.calculation.energyLabel}
        residenceType={residenceType}
        targetLabel={calculation.energyLabel}
      />
      <Text mt={4}>Energielabel</Text>
      <EnergyLabel label={calculation.energyLabel} />
      <TextPopover popoverSize="2xs" trigger="Maandlasten">
        Dit zijn de geschatte maandlasten voor energie.
      </TextPopover>
      <Text fontSize={22} fontWeight="bold">
        {calculation &&
        (gasPrice || electricityPrice || joulePrice || epvPrice) ? (
          <Flex alignItems="center" gap={2}>
            <PriceDisplay
              price={
                (gasPrice * (calculation.yearlyGasConsumption ?? 0)) / 12 +
                (electricityPrice *
                  (calculation.yearlyElectricityConsumption ?? 0)) /
                  12 +
                // (joulePrice * (calculation.heatNetworkUsage ?? 0)) / 12 +
                epvPrice * (calculation.usableArea ?? calculation.lossArea ?? 0)
              }
            />
            <InfoPopover size="2xs">
              <UnorderedList>
                {!!(gasPrice && calculation.yearlyGasConsumption) && (
                  <ListItem>
                    Kosten gas:{" "}
                    <PriceDisplay
                      price={
                        (gasPrice * (calculation.yearlyGasConsumption ?? 0)) /
                        12
                      }
                    />
                  </ListItem>
                )}
                {!!(
                  electricityPrice && calculation.yearlyElectricityConsumption
                ) && (
                  <ListItem>
                    Kosten elektriciteit:{" "}
                    <PriceDisplay
                      price={
                        (electricityPrice *
                          (calculation.yearlyElectricityConsumption ?? 0)) /
                        12
                      }
                    />
                  </ListItem>
                )}
                {/* {!!(joulePrice && calculation.heatNetworkUsage) && (
                  <ListItem>
                    Kosten warmtenet:{" "}
                    <PriceDisplay
                      price={(joulePrice * calculation.heatNetworkUsage) / 12}
                    />
                  </ListItem>
                )} */}
                {!!(
                  epvPrice &&
                  (calculation.usableArea || calculation.lossArea)
                ) && (
                  <ListItem>
                    Kosten EPV:{" "}
                    <PriceDisplay
                      price={
                        epvPrice *
                        (calculation.usableArea ?? calculation.lossArea ?? 0)
                      }
                    />
                  </ListItem>
                )}
              </UnorderedList>
            </InfoPopover>
          </Flex>
        ) : (
          <Flex alignItems="center" gap={2}>
            ?{" "}
            <InfoPopover>
              Geen prijzen beschikbaar, vul deze in via het tandwiel rechtsboven
            </InfoPopover>
          </Flex>
        )}
      </Text>
    </Flex>
  );
}
